import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
// Bundle hls.js locally instead of letting react-player lazy-load it from a
// CDN. This guarantees HLS playback works in air-gapped / strict-CSP
// environments and keeps the media-player dependency self-contained after the
// React 18 / Vite migration.
import Hls from "hls.js";
import { CircularProgress, Grid, IconButton, Typography } from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import routerPath from "../../utils/router/routerPath";
import appConfig from "../../utils/constant/appConfig";
import { guestUserAttendees } from "../../utils/apis/apis";
import { useDispatch, useSelector } from "react-redux";
import * as sessionActions from "../../store/session";
import { toast } from "react-toastify";

// ---------------------------------------------------------------------------
// Anytime-webinar cookie helpers.
//
// A webinar can be marked "anytime" on the backend (`Webinar.is_anytime_webinar`).
// For those webinars we periodically persist the viewer's playback position
// into a browser cookie so they can resume from where they left off the next
// time they open the same webinar. The cookie is keyed by webinar id so each
// webinar tracks its own progress independently.
// ---------------------------------------------------------------------------
const ANYTIME_COOKIE_PREFIX = "lw_webinar_progress_";
// How often (ms) to flush the latest playback position into the cookie.
const ANYTIME_SAVE_INTERVAL_MS = 5000;
// How long (days) the cookie is kept around. 1 year is plenty for resume.
const ANYTIME_COOKIE_TTL_DAYS = 365;
// Custom DOM event the watch-room dispatches when the user clicks the
// "Start from beginning" button. Each anytime VideoPlayer subscribes to it.
const ANYTIME_START_OVER_EVENT = "letswebinar:anytime-start-over";

const buildCookieName = (webinarId) =>
  `${ANYTIME_COOKIE_PREFIX}${webinarId}`;

const setAnytimeProgressCookie = (webinarId, seconds) => {
  if (!webinarId || seconds == null || !Number.isFinite(seconds)) return;
  // Round to one decimal to keep the cookie tiny – we never need
  // millisecond accuracy for resume-on-page-load.
  const value = Math.max(0, Math.round(seconds * 10) / 10);
  const expires = new Date(
    Date.now() + ANYTIME_COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000
  );
  document.cookie =
    `${buildCookieName(webinarId)}=${encodeURIComponent(String(value))};` +
    ` expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const readAnytimeProgressCookie = (webinarId) => {
  if (!webinarId || typeof document === "undefined") return null;
  const name = `${buildCookieName(webinarId)}=`;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const part of parts) {
    if (part.startsWith(name)) {
      const raw = decodeURIComponent(part.substring(name.length));
      const num = parseFloat(raw);
      return Number.isFinite(num) && num >= 0 ? num : null;
    }
  }
  return null;
};

const clearAnytimeProgressCookie = (webinarId) => {
  if (!webinarId || typeof document === "undefined") return;
  // Setting a past expiry effectively deletes the cookie.
  document.cookie =
    `${buildCookieName(webinarId)}=;` +
    " expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax";
};

const VideoPlayer = ({
  videoUrl,
  getSecondValue,
  totalDuration,
  startVideoDuration,
  // When True the watch-room is in "anytime webinar" mode: we persist the
  // viewer's playback position to a cookie at a fixed interval and resume
  // from that position on the next visit. A "Start from beginning" button
  // (rendered by the parent watch-room page) can also reset the saved
  // position via the ANYTIME_START_OVER_EVENT custom event.
  isAnytimeWebinar = false,
  // When True the webinar is "scrollable": the underlying video element is
  // rendered with native browser controls (play/pause, seek bar, volume,
  // fullscreen, etc.) so the viewer can freely scroll / scrub through the
  // timeline. When False (default) the player is locked down with no
  // controls – legacy behaviour.
  isScrollableWebinar = false,
}) => {
  const navigate = useNavigate();
  const { webinarId } = useParams();
  const dispatch = useDispatch();
  const [soundOn, setSoundOn] = useState(false);
  const [showLoader, setLoader] = useState(true);
  const [loading, setLoading] = useState(true);
  const sessionUser = useSelector((state) => state.session.user);
  const playerRef = useRef();
  // Holds the most recent playback position reported by react-player. We
  // sample this from a setInterval to write the anytime cookie so the
  // disk-write happens at a steady cadence rather than on every progress
  // tick (which would hit the cookie store many times a second).
  const lastPlayedSecondsRef = useRef(0);
  const queryParams = new URLSearchParams(window.location.search);
  const tagsParam = queryParams.get("tag");
  const tagsUserParam = queryParams.get("tag_user");
  const tagsCompanyParam = queryParams.get("tag_company");
  const tagsCustomParam = queryParams.get("tag_custom");
  const tagsCustom2Param = queryParams.get("tag_custom2");
  const tagsSourceParam = queryParams.get("tag_source");
  const tagsMediumParam = queryParams.get("tag_medium");
  const tagsCampaignParam = queryParams.get("tag_campaign");
  const tagsContentParam = queryParams.get("tag_content");
  const tagsTermsParam = queryParams.get("tag_term");
  const [selectedBitrateIndex, setSelectedBitrateIndex] = useState(0); 
  const handleToggleMute = () => {
    setSoundOn((prevSoundOn) => !prevSoundOn);
  };

  const handleVideoClick = () => {
    handleToggleMute();
  };

  const handleBuffer = () => {
    setLoader(true);
  };

  const handleBufferEnd = () => {
    setLoader(false);
  };

  const onReady = useCallback(() => {
    if (!playerRef.current) return;

    // Decide where to seek to:
    //  * Anytime webinar with a saved cookie => resume from cookie.
    //  * Anytime webinar without a cookie    => start at 0 (from beginning).
    //  * Standard webinar                    => use the server-provided
    //                                           startVideoDuration (which is
    //                                           the elapsed-since-schedule
    //                                           offset from VideoAccessAPI).
    let seekTo = startVideoDuration || 0;
    if (isAnytimeWebinar) {
      const saved = readAnytimeProgressCookie(webinarId);
      seekTo = saved != null ? saved : 0;
    }

    try {
      playerRef.current.seekTo(seekTo, "seconds");
    } catch (err) {
      // Defensive: react-player throws if the underlying player isn't ready
      // yet on some HLS providers. We retry inside the next tick.
      setTimeout(() => {
        try {
          playerRef.current && playerRef.current.seekTo(seekTo, "seconds");
        } catch (_) {}
      }, 250);
    }

    const internalPlayer = playerRef.current.getInternalPlayer();
    if (internalPlayer && typeof internalPlayer.setAttribute === "function") {
      if (soundOn) {
        internalPlayer.setAttribute("disablePictureInPicture", "true");
      } else {
        internalPlayer.removeAttribute("disablePictureInPicture");
      }
    }
  }, [playerRef.current, soundOn, startVideoDuration, isAnytimeWebinar, webinarId]);

  // -------------------------------------------------------------------
  // Anytime-webinar: periodic cookie persistence + start-over event.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!isAnytimeWebinar || !webinarId) return undefined;

    // Flush the most recent playback time into the cookie at a steady cadence.
    const intervalId = setInterval(() => {
      const seconds = lastPlayedSecondsRef.current;
      if (Number.isFinite(seconds) && seconds > 0) {
        setAnytimeProgressCookie(webinarId, seconds);
      }
    }, ANYTIME_SAVE_INTERVAL_MS);

    // Best-effort: also persist when the tab is hidden / closed so we don't
    // lose the last few seconds of progress before the next interval tick.
    const flushOnExit = () => {
      const seconds = lastPlayedSecondsRef.current;
      if (Number.isFinite(seconds) && seconds > 0) {
        setAnytimeProgressCookie(webinarId, seconds);
      }
    };
    window.addEventListener("beforeunload", flushOnExit);
    document.addEventListener("visibilitychange", flushOnExit);

    // Listen for the "Start from beginning" button (rendered by the
    // parent watch-room) and reset the player + cookie when fired.
    const handleStartOver = () => {
      clearAnytimeProgressCookie(webinarId);
      lastPlayedSecondsRef.current = 0;
      try {
        playerRef.current && playerRef.current.seekTo(0, "seconds");
      } catch (_) {}
    };
    window.addEventListener(ANYTIME_START_OVER_EVENT, handleStartOver);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("beforeunload", flushOnExit);
      document.removeEventListener("visibilitychange", flushOnExit);
      window.removeEventListener(ANYTIME_START_OVER_EVENT, handleStartOver);
    };
  }, [isAnytimeWebinar, webinarId]);

  useEffect(() => {
    const handleContextMenu = (event) => {
      event.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  const loginWithExistingUrl = async (formData) => {
    try {
      const value = await guestUserAttendees(formData);
      const userAllData = value.data;
      if (value.status === "success") {
        if (!sessionUser.refresh) {
          localStorage.setItem("user", JSON.stringify(userAllData));
          dispatch(sessionActions.setUser(userAllData));
        } else {
          return;
        }
        toast.success(value.message);
        setLoading(false);
        navigate(`/webinarView/${webinarId}`);
      } else {
        toast.error(value.message);
        setLoading(false);
      }
    } catch (error) {
      console.error("API request error:", error);
      toast.error("Error occurred while submitting the form.");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionUser.refresh) {
      const tagsDict = {
        tag: tagsParam || "",
        tag_user: tagsUserParam?.toString() || "",
        tag_company: tagsCompanyParam?.toString() || "",
        tag_custom: tagsCustomParam?.toString() || "",
        tag_custom2: tagsCustom2Param?.toString() || "",
        tag_source: tagsSourceParam?.toString() || "",
        tag_medium: tagsMediumParam?.toString() || "",
        tag_campaign: tagsCampaignParam?.toString() || "",
        tag_content: tagsContentParam?.toString() || "",
        tag_term: tagsTermsParam?.toString() || "",
      };

      const formData = {
        webinar: webinarId,
        full_name: sessionUser.full_name,
        guest_email: sessionUser.guest_email,
        guest_phone: sessionUser.guest_phone,
        tags: tagsDict,
      };

      setTimeout(() => {
        loginWithExistingUrl(formData);
      }, 1000);
    }
  }, []);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: "100%",
        minHeight: "100%",
      }}
      className="video-div"
    >
      {showLoader && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 999,
          }}
        >
          <CircularProgress />
        </div>
      )}
      {/*
        The "Join Webinar" overlay is intended for the locked-down
        webinar experience where viewers cannot otherwise interact with
        the player. For scrollable webinars we hide it because the
        native controls already give the viewer full control – leaving
        the overlay visible would cover the timeline / play button.
      */}
      <div
        className="volume-icon"
        style={{
          backgroundColor: soundOn ? "" : "rgb(0 0 0 / 71%)",
          display: soundOn || isScrollableWebinar ? "none" : "block",
          padding: "0px 5px",
        }}
      >
        {soundOn ? (
          <IconButton sx={{ color: "white" }}>
            <div className="volume-txt">
              <Typography
                variant="h5"
                style={{ display: soundOn ? "none" : "block" }}
              >
                Webinar Start
              </Typography>
            </div>
          </IconButton>
        ) : (
          <IconButton sx={{ color: "#f5f5f5d4" }} onClick={handleToggleMute}>
            <div className="volume-txt">
              <Typography variant="h5">Join Webinar </Typography>
            </div>
          </IconButton>
        )}
      </div>

      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          pointerEvents: "auto",
        }}
      >
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          playsinline
          width="100%"
          height="100%"
          playing={true}
          // Show native player controls (play/pause, seek bar, volume,
          // fullscreen, etc.) only when the admin has marked this
          // webinar as "scrollable". This is what lets the viewer
          // actually scroll / scrub through the video timeline.
          controls={!!isScrollableWebinar}
          volume={1}
          // For scrollable webinars we don't auto-mute: the viewer is
          // expected to play / pause / interact with the player using
          // native controls, so an audible default is more useful.
          muted={isScrollableWebinar ? false : !soundOn}
          onDisablePIP={true}
          style={{
            objectFit: "cover",
          }}
          onDuration={(val) => {
            setTimeout(() => {
              totalDuration(val);
            }, 5000);
          }}
          onProgress={(val) => {
            // Track latest playback time for the anytime-cookie writer.
            lastPlayedSecondsRef.current = val.playedSeconds;
            getSecondValue(val.playedSeconds);
          }}
          onReady={onReady}
          onBuffer={handleBuffer}
          onBufferEnd={handleBufferEnd}
          onEnded={() => {
            // For anytime webinars, clear the saved progress so the next
            // visit starts from the beginning instead of "ending" again.
            if (isAnytimeWebinar && webinarId) {
              clearAnytimeProgressCookie(webinarId);
            }
            navigate(`${routerPath.webinarAttendSuccess}/${webinarId}`);
          }}
          disableContextMenu={true}
          config={{
            file: {
              attributes: {
                preload: "none",
              },
              // forceHLS makes react-player always use hls.js (rather than the
              // browser's native HLS in Safari) so behaviour is consistent.
              forceHLS: true,
              hlsOptions: {
                startLevel: 0,
              },
              // Pass our locally-bundled hls.js directly to react-player so it
              // does NOT lazy-load it from a CDN at runtime.
              hlsClass: Hls,
            },
            setAttribute: {
              allowsInlineMediaPlayback: true,
            },
            youtube: {
              playerVars: false,
              embedOptions: false,
            },
            vimeo: {
              playerOptions: false,
              iframeParams: { fullscreen: 0 },
            },
          }}
          controlsList="nodownload"
          // For scrollable webinars we let native controls handle clicks
          // (clicking the timeline scrubs, clicking the player toggles
          // play/pause). For non-scrollable webinars we keep the legacy
          // behaviour where clicking the video toggles mute / unmute via
          // the "Join Webinar" overlay.
          onClick={isScrollableWebinar ? undefined : handleVideoClick}
        />
      </div>
    </div>
  );
};

// Re-export the cookie-helper constants so the parent watch-room can reuse
// the exact same event/cookie names (no string drift between files).
export const ANYTIME_WEBINAR_START_OVER_EVENT = ANYTIME_START_OVER_EVENT;
export const clearAnytimeWebinarProgressCookie = clearAnytimeProgressCookie;

export default VideoPlayer;
